import { requestComplianceItemExtension } from '@commenda/api';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FieldWrapper,
  Label,
  LoadingIndicator,
  Textarea,
  useToggle,
} from '@commenda/kakashi';
import mixpanel, {
  MIXPANEL_SOURCE,
  MIXPANEL_SURFACE_AREA,
} from '@commenda/mixpanel';
import { SyntheticComplianceStatus } from '@commenda/types/src/compliance-calendar/compliances/types';
import React, { useState } from 'react';
import ComplianceStatusBadge from '../../../../compliances/compliance-status-badge';
import { useNotification } from '../../../../hooks/useNotification';

interface RequestExtensionModalProps {
  isOpen: boolean;
  onClose: () => void;
  filingId: string;
  filingTitle: string;
  entityName?: string;
  jurisdiction?: string;
  category?: string;
  syntheticStatus?: SyntheticComplianceStatus;
  dueDate?: string;
  onSuccess: () => void;
}

const SubtitleDot = () => (
  <span className="shrink-0 size-1 rounded-full bg-[#c1bdb8]" />
);

const RequestExtensionModal = ({
  isOpen,
  onClose,
  filingId,
  filingTitle,
  entityName,
  jurisdiction,
  category,
  syntheticStatus,
  dueDate,
  onSuccess,
}: RequestExtensionModalProps) => {
  const { setNotification } = useNotification();
  const isSubmitting = useToggle(false);
  const [note, setNote] = useState('');

  const subtitleParts = [entityName, jurisdiction, category].filter(
    Boolean
  ) as string[];

  const reset = () => setNote('');

  const close = () => {
    reset();
    onClose();
  };

  const onRequest = async () => {
    isSubmitting.on();

    const res = await requestComplianceItemExtension(filingId, {
      note: note.trim() || undefined,
    });

    if (!res.success) {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message: 'Could not send the extension request. Please try again.',
      });
      isSubmitting.off();
      return;
    }

    setNotification({
      show: true,
      valid: true,
      title: 'Extension requested',
      message: 'Commenda has been notified.',
    });

    mixpanel.track('CALENDAR_FILING_EXTENSION_REQUESTED', {
      source: MIXPANEL_SOURCE.CALENDAR,
      surface_area: MIXPANEL_SURFACE_AREA.COMPLIANCE_CALENDAR,
      compliance_id: filingId,
      has_note: note.trim().length > 0,
    });

    await onSuccess();
    isSubmitting.off();
    close();
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) close();
      }}
    >
      <DialogContent
        onClick={(e) => e.stopPropagation()}
        preventCloseOnOutsideClick
      >
        <DialogHeader className="flex flex-col gap-xs items-start border-b-0 pb-0">
          <DialogTitle>Request for extension</DialogTitle>
          <DialogDescription>
            Update the filing deadline for this obligation:
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-xl p-3xl">
          <div className="flex flex-col gap-xs">
            <div className="flex items-center gap-md">
              <span className="txt-small-bold text-text-primary">
                {filingTitle}
              </span>
              {syntheticStatus && (
                <ComplianceStatusBadge
                  status={syntheticStatus}
                  dueDate={dueDate ?? ''}
                />
              )}
            </div>
            {subtitleParts.length > 0 && (
              <div className="flex items-center gap-sm">
                {subtitleParts.map((part, i) => (
                  <React.Fragment key={i}>
                    {i > 0 && <SubtitleDot />}
                    <span className="txt-extra-small-medium text-text-secondary whitespace-nowrap overflow-hidden text-ellipsis">
                      {part}
                    </span>
                    {i !== subtitleParts.length - 1 && <span>・</span>}
                  </React.Fragment>
                ))}
              </div>
            )}
          </div>

          <FieldWrapper>
            <Label className="flex items-center gap-xxs">
              <span>Note</span>
              <span className="text-text-quaternary">(optional)</span>
            </Label>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Type here..."
              maxLength={2000}
            />
          </FieldWrapper>
        </div>
        <DialogFooter className="border-t-0">
          <Button variant="secondary" onClick={close} size="md">
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={onRequest}
            size="md"
            disabled={isSubmitting.value}
          >
            {isSubmitting.value ? <LoadingIndicator size={16} /> : null}
            <span>Request for extension</span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RequestExtensionModal;
